import React, { useMemo, useReducer, useContext, useEffect } from "react";
import { toastr } from "react-redux-toastr";
import { client } from "utils/api";

import reducer, {
  initial_state,
  DATA_LOADING,
  CHANGE_SORT,
  SET_FILTER_VALUE,
  SET_FAST_FILTER_VALUE,
  REMOVE_FILTER_VALUE,
  DATA_FAILURE,
  DATA_SUCCESS,
  SET_META_PAGINATION,
  FILTER_PRODUCTS,
  SET_PRODUCT_ID,
  UPDATE_PRODUCT,
  SET_OPEN_CALCULATOR,
  UPDATE_PRODUCT_CUSTOM_PURCHASE_PRICE,
} from "./reducer";

// CONTEXT ===================================
const ProductsPricesContext = React.createContext();

const sort = [
  {
    label: "Produkt",
    name: "name_sku_or_product_group_sku",
    predefined_sorts: false,
  },
  {
    label: "Producent",
    name: "producer_name",
    style: { width: 130 },
    predefined_sorts: false,
  },
  {
    label: "Dostawca",
    name: "supplier_name",
    style: { width: 130 },
    predefined_sorts: false,
  },
  {
    label: "Stan",
    name: "sales_stock",
    style: { width: 100 },
    predefined_sorts: false,
  },
  {
    label: "Synch. stanów",
    name: "has_infinite_sales_stock",
    style: { width: 120 },
    predefined_sorts: false,
    sortable: false,
  },
  {
    label: "Status",
    name: "status",
    style: { width: 130 },
    predefined_sorts: false,
    sortable: false,
  },
  {
    label: "Zakup (netto)",
    name: "purchase_price_netto_value",
    style: { width: 120 },
    predefined_sorts: true,
  },
  {
    label: "Zakup (brutto)",
    name: "purchase_price_brutto_value",
    style: { width: 120 },
    predefined_sorts: true,
  },
  {
    label: "Cena PLN",
    name: "sales_price_pln",
    style: { width: 120 },
    predefined_sorts: true,
  },
  {
    label: "Cena EUR",
    name: "sales_price_eur",
    style: { width: 120 },
    predefined_sorts: true,
  },
  {
    label: "Cena CZK",
    name: "sales_price_czk",
    style: { width: 120 },
    predefined_sorts: true,
  },
  {
    label: "Cena SEK",
    name: "sales_price_sek",
    style: { width: 100 },
    predefined_sorts: true,
  },
  {
    label: "Cena GBP",
    name: "sales_price_gbp",
    style: { width: 100 },
    predefined_sorts: true,
  },
  {
    label: "Cena HUF",
    name: "sales_price_huf",
    style: { width: 100 },
    predefined_sorts: true,
  },
  {
    label: "Cena RON",
    name: "sales_price_ron",
    style: { width: 100 },
    predefined_sorts: true,
  },
  {
    label: "Akcje",
    name: "actions",
    sortable: false,
    style: { width: 100 },
    predefined_sorts: false,
  },
];

// narzut
export const calculateMarkup = ({ purchase_price, sales_price }) =>
  sales_price
    ? (((sales_price - purchase_price) / purchase_price) * 100).toFixed(2)
    : 0;

// marża
export const calculateMargin = ({ purchase_price, sales_price }) =>
  sales_price
    ? (((sales_price - purchase_price) / sales_price) * 100).toFixed(2)
    : 0;

// Współczynniki wzoru na narzut (narzut = ułamek, np. 0.5 = 50%):
//
//   narzut = base / ( ln(koszt_pln + log_shift) ^ log_exponent ) / 100
//
//   odpowiednik w arkuszu: =$H$1/(POWER(LN(A3+$I$1);$J$1))/100
//     base = H1, log_shift = I1, log_exponent = J1
//
//   Dla kosztu zakupu < min_purchase_price_for_formula wzór nie działa
//   (ln(koszt + log_shift) <= 0), więc stosujemy sztywny flat_markup.
//
// UWAGA: wariant `pln` musi być zgodny z WSF:
//   tradesk.wsf/app/services/offers/price_table.rb -> MARKUP_COEFFICIENTS
export const MARKUP_COEFFICIENTS = {
  pln: {
    base: 170, // licznik wzoru
    log_shift: 0.4, // przesunięcie wewnątrz logarytmu
    log_exponent: 0.67, // wykładnik potęgi logarytmu
    min_purchase_price_for_formula: 1.0, // PLN brutto; poniżej -> flat_markup
    flat_markup: 3.5, // 350% dla tanich produktów
  },
  other: {
    base: 240,
    log_shift: 0.4,
    log_exponent: 0.67,
    min_purchase_price_for_formula: 1.0,
    flat_markup: 5.0, // 500% dla tanich produktów
  },
};

// Zwraca narzut (ułamek) dla kosztu zakupu brutto w PLN i waluty sprzedaży.
export const calculateMarkupRate = ({ purchase_price_pln, currency }) => {
  const c =
    currency === "pln" ? MARKUP_COEFFICIENTS.pln : MARKUP_COEFFICIENTS.other;

  if (purchase_price_pln < c.min_purchase_price_for_formula) {
    return c.flat_markup;
  }

  const log_value = Math.log(purchase_price_pln + c.log_shift);
  return c.base / log_value ** c.log_exponent / 100;
};

export const calculateSuggestedPrice = ({
  purchase_price,
  exchange_rate,
  currency,
  suggested_price_multiplier = 1,
}) => {
  // cena_sprzedaży = koszt_pln * (1 + narzut) / kurs_waluty * mnożnik
  // (kurs_waluty dla PLN = 1)

  if (!purchase_price || parseFloat(purchase_price) === 0) {
    return 0;
  }

  const purchase_price_pln = parseFloat(purchase_price);
  const markup = calculateMarkupRate({ purchase_price_pln, currency });

  return (
    ((purchase_price_pln * (1 + markup)) / exchange_rate) *
    suggested_price_multiplier
  ).toFixed(2);
};

function ProductsPricesProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial_state);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      dispatch({ type: DATA_LOADING });
      const { products, exchange_rates, product_suppliers } = await client.get(
        "/products/products_prices",
      );

      const supplier_names = [
        ...new Set(product_suppliers.map(({ supplier_name }) => supplier_name)),
      ].filter(Boolean);

      const products_data = products.map((product) => {
        const product_supplier = product_suppliers.find(
          ({ sku }) => sku === product.sku,
        );
        return {
          ...product,
          expano_root_id: product_supplier?.expano_root_id,
          expano_wms_id: product_supplier?.expano_wms_id,
          product_supplier,
          sales_stock:
            product.is_bundle || product.is_virtual
              ? 0
              : product.sales_stock || 0,
        };
      });

      dispatch({
        type: DATA_SUCCESS,
        payload: {
          products: products_data,
          exchange_rates,
          supplier_names,
        },
      });
    } catch (error) {
      dispatch({ type: DATA_FAILURE });
      toastr.error(
        "Błąd",
        error?.response?.data?.message || "Wystąpił błąd podczas pobierania",
      );
      if (error.response.status === 403) {
        window.location.pathname = "/";
      }
    }
  };

  const changeSort = ({ column, direction }) =>
    dispatch({
      type: CHANGE_SORT,
      payload: { column, direction },
    });

  const setFilterValue = ({ name, value, label }) => {
    dispatch({
      type: SET_FILTER_VALUE,
      payload: { name, value, label },
    });
    dispatch({ type: FILTER_PRODUCTS });
  };
  const setFastFilterValue = ({ name, value }) => {
    dispatch({
      type: SET_FAST_FILTER_VALUE,
      payload: { name, value },
    });
    dispatch({ type: FILTER_PRODUCTS });
  };

  const removeFilterValue = ({ name, value }) => {
    dispatch({
      type: REMOVE_FILTER_VALUE,
      payload: { name, value },
    });
    dispatch({ type: FILTER_PRODUCTS });
  };

  const setMetaPagination = ({ page, per_page }) =>
    dispatch({ type: SET_META_PAGINATION, payload: { page, per_page } });

  const setProductId = (product_id) =>
    dispatch({ type: SET_PRODUCT_ID, payload: { product_id } });

  const updateProduct = async (product_id, values) => {
    try {
      await client.put(`/products/${product_id}`, values);
      dispatch({ type: UPDATE_PRODUCT, payload: { product_id, values } });
    } catch (error) {
      throw error;
    }
  };

  const updateCustomPurchasePrice = async (
    product_id,
    custom_purchase_price_netto,
  ) => {
    try {
      await client.put(`/products/${product_id}`, {
        custom_purchase_price_netto,
      });
      const { data: product_data } = await client.get(
        `/products/${product_id}?context=products_prices`,
      );
      dispatch({
        type: UPDATE_PRODUCT_CUSTOM_PURCHASE_PRICE,
        payload: { product_id, data: product_data },
      });
    } catch (error) {
      throw error;
    }
  };

  const setOpenCalculator = (is_open_calculator) =>
    dispatch({ type: SET_OPEN_CALCULATOR, payload: { is_open_calculator } });

  const value = useMemo(() => {
    return {
      state,
      sort,
      changeSort,
      setFilterValue,
      setFastFilterValue,
      removeFilterValue,
      setMetaPagination,
      setProductId,
      calculateMarkup,
      calculateMargin,
      updateProduct,
      setOpenCalculator,
      calculateSuggestedPrice,
      updateCustomPurchasePrice,
    };
  }, [state]);

  return (
    <ProductsPricesContext.Provider value={value}>
      {children}
    </ProductsPricesContext.Provider>
  );
}

const useProductsPrices = () => useContext(ProductsPricesContext);
export { useProductsPrices };
export default ProductsPricesProvider;
